import {initialViewState} from '@/pages/CenterDiscovery/constants';
import {TransformedLocationInfo} from '@/pages/CenterDiscovery/Utils/dataTransformer';
import React, {forwardRef, useEffect, useState, Suspense} from 'react';
import {LocationMarker} from '../LocationMarker/LocationMarker';
import {MapRef} from 'react-map-gl';

// Dynamic import for react-map-gl components
const MapboxMap = React.lazy(() => import('react-map-gl').then((module) => ({default: module.Map})));
const Marker = React.lazy(() => import('react-map-gl').then((module) => ({default: module.Marker})));
interface MapProps {
  tocaLocations: TransformedLocationInfo[];
  activeLocationIndexInList: number;
  setActiveLocationListIndex: (index: number) => void;
}

// Loading component for the map
const MapLoadingFallback = () => (
  <div className="w-full h-full flex items-center justify-center bg-gray-100">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading map...</p>
    </div>
  </div>
);

/**
 * Renders a Mapbox map component with markers for TOCA locations.
 * This component dynamically loads mapbox-gl only when needed.
 *
 * @component
 * @param {React.Ref<MapRef>} ref - The ref object for accessing the map component.
 * @param {MapProps} props - The props for configuring the map component.
 * @returns {JSX.Element} The rendered map component.
 */
const Map: React.ForwardRefRenderFunction<MapRef, MapProps> = (
  {tocaLocations, activeLocationIndexInList, setActiveLocationListIndex},
  ref
) => {
  const [activeTooltipLocationIndex, setActiveTooltipLocationIndex] = useState<number>(-1);

  useEffect(() => {
    setActiveTooltipLocationIndex(-1);
  }, [activeLocationIndexInList]);

  const onClickLocationMarkerHandler = React.useCallback(
    (index: number) => {
      // If the location marker is already active, close the tooltip
      if (index === activeTooltipLocationIndex) {
        setActiveTooltipLocationIndex(-1);
      } else {
        // Otherwise, open the tooltip and select the location on map
        setActiveLocationListIndex(index);
        setTimeout(() => {
          setActiveTooltipLocationIndex(index);
        }, 0);
      }
    },
    [activeTooltipLocationIndex, setActiveLocationListIndex]
  );

  return (
    <Suspense fallback={<MapLoadingFallback />}>
      <MapboxMap
        ref={ref}
        mapboxAccessToken={import.meta.env.VITE_MAP_BOX_ACCESS_KEY}
        initialViewState={initialViewState}
        mapStyle={import.meta.env.VITE_MAP_BOX_STYLE}
      >
        {tocaLocations.map((location, index) => (
          <Marker key={location.siteId} longitude={location.coordinates.lon} latitude={location.coordinates.lat}>
            <LocationMarker
              number={index + 1}
              address={location.address}
              state={location.state}
              name={location.name}
              logo={location.logo}
              open={activeTooltipLocationIndex === index}
              onClickLocationMarkerHandler={onClickLocationMarkerHandler}
            />
          </Marker>
        ))}
      </MapboxMap>
    </Suspense>
  );
};

export default forwardRef(Map);
